import { useCallback, useEffect, useRef, useState } from 'react';

const SOUND_KEY = 'wems_crm_desk_sound';
const DESKTOP_KEY = 'wems_crm_desk_desktop';

function readFlag(key: string, fallback: boolean): boolean {
  const stored = localStorage.getItem(key);
  if (stored === null) return fallback;
  return stored === '1';
}

function writeFlag(key: string, value: boolean): void {
  localStorage.setItem(key, value ? '1' : '0');
}

/**
 * 中文：以 Web Audio 合成提示音，不引用任何外部音檔，避免 CSP 與部署額外資產。
 *       瀏覽器政策要求使用者先與頁面互動過才允許播放，因此失敗時靜默略過。
 * English: Synthesises the alert tone with Web Audio so no external asset or CSP allowance is needed. Browser policy
 *       requires a prior user gesture, so a failure is silently ignored.
 */
function playAlertTone(): void {
  try {
    const AudioContextCtor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextCtor) return;
    const context = new AudioContextCtor();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(880, context.currentTime);
    oscillator.frequency.setValueAtTime(1174, context.currentTime + 0.12);
    gain.gain.setValueAtTime(0.0001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.18, context.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.32);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.34);
    oscillator.onended = () => void context.close();
  } catch {
    // 中文：提示音失敗不得影響客服作業。English: A failed tone must never affect agent work.
  }
}

export interface DeskNotificationApi {
  soundEnabled: boolean;
  desktopEnabled: boolean;
  desktopPermission: NotificationPermission | 'unsupported';
  setSoundEnabled: (value: boolean) => void;
  requestDesktop: (value: boolean) => Promise<void>;
  notify: (title: string, body: string, tag: string) => void;
}

/**
 * 中文：客服提示中樞。負責分頁標題未讀數、提示音與桌面通知；三者都可由客服自行關閉。
 * English: Agent alerting hub covering the tab-title unread badge, the alert tone, and desktop notifications, each of
 *       which the agent can switch off.
 */
export function useDeskNotifications(unreadCount: number): DeskNotificationApi {
  const [soundEnabled, setSoundEnabledState] = useState(() => readFlag(SOUND_KEY, true));
  const [desktopEnabled, setDesktopEnabledState] = useState(() => readFlag(DESKTOP_KEY, false));
  const [desktopPermission, setDesktopPermission] = useState<NotificationPermission | 'unsupported'>(
    () => ('Notification' in window ? Notification.permission : 'unsupported')
  );
  const baseTitleRef = useRef<string>(document.title.replace(/^\(\d+\)\s*/, ''));

  useEffect(() => {
    const base = baseTitleRef.current;
    document.title = unreadCount > 0 ? `(${unreadCount}) ${base}` : base;
    return () => {
      document.title = base;
    };
  }, [unreadCount]);

  const setSoundEnabled = useCallback((value: boolean) => {
    writeFlag(SOUND_KEY, value);
    setSoundEnabledState(value);
    // 中文：開啟時立即試音，同時滿足瀏覽器要求的使用者互動條件。
    // English: Play once on enable, which also satisfies the browser's user-gesture requirement.
    if (value) playAlertTone();
  }, []);

  const requestDesktop = useCallback(async (value: boolean) => {
    if (!value) {
      writeFlag(DESKTOP_KEY, false);
      setDesktopEnabledState(false);
      return;
    }
    if (!('Notification' in window)) {
      setDesktopPermission('unsupported');
      return;
    }
    const permission =
      Notification.permission === 'granted'
        ? Notification.permission
        : await Notification.requestPermission();
    setDesktopPermission(permission);
    const granted = permission === 'granted';
    writeFlag(DESKTOP_KEY, granted);
    setDesktopEnabledState(granted);
  }, []);

  const notify = useCallback(
    (title: string, body: string, tag: string) => {
      if (soundEnabled) playAlertTone();
      if (!desktopEnabled || !('Notification' in window) || Notification.permission !== 'granted') {
        return;
      }
      try {
        // 中文：以 conversationId 當 tag，同一則對話的連續訊息只會覆蓋而不會刷屏。
        // English: Tagging by conversationId collapses consecutive messages for one thread instead of flooding.
        const notification = new Notification(title, { body, tag, renotify: false } as NotificationOptions);
        notification.onclick = () => {
          window.focus();
          notification.close();
        };
      } catch {
        // 中文：通知失敗不得影響客服作業。English: A failed notification must never affect agent work.
      }
    },
    [desktopEnabled, soundEnabled]
  );

  return {
    soundEnabled,
    desktopEnabled,
    desktopPermission,
    setSoundEnabled,
    requestDesktop,
    notify,
  };
}
